import { ArrowBackIosNew, ArrowForwardIos, EditNoteRounded, Save } from '@mui/icons-material'
import { Box, Button, Divider, IconButton, Stack, Step, StepButton, StepLabel, Stepper, Tooltip, Typography, useTheme } from '@mui/material'
import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { IClassifiedFeature } from '../../../definitions/Classification'
import { IPADUCompiled } from '../../../definitions/Contract'
import { IFormField } from '../../../definitions/FormField'
import { IFormGroup } from '../../../definitions/FormGroup'
import ContractsManager from '../../../managers/ContractsManager'
import PADUChip from '../../controls/PADUChip'
import VectorIcon from '../../icons/VectorIcon'
import FieldEditor from './FieldEditor'
import FormHelper from './FormHelper'
import { FormBuilderOptions, FormBuilderSCC } from './Interfaces'
import TreeViewer from './TreeViewer'

interface P {
    paduCompiled?: IPADUCompiled | null
    data: any
    schema: IFormGroup[]
    scc?: FormBuilderSCC<any>
    stringOptions?: FormBuilderOptions<any>
    ingestionMode?: boolean
    features?: IClassifiedFeature[]
    setData: (data: any) => void
    onSave: (data: any) => void
    onComplete: () => void
    /**
     * Instead of the default MUI stepper, show a custom smaller stepper with a progress bar
     */
    lightStepper?: boolean
    /**
     * Disable automatic saving on each step navigation.
     * When true, saves only when user clicks Submit or on last step.
     */
    disableAutoSave?: boolean
    /**
     * Disable saving  Editor view mode
     */
    isReadOnly?: boolean
    onReRunAI?: () => void
    component?: 'contract_editor' | 'workflow_editor'
}

function EditorForm({ paduCompiled, data, schema, scc, stringOptions, setData, onSave, onComplete, ingestionMode, features, lightStepper, disableAutoSave, isReadOnly, onReRunAI, component }: P) {
    const [groupIndex, setGroupIndex] = useState(0)
    const isLastStep = groupIndex === schema.length - 1
    const [isIngestionMode, setIsIngestionMode] = useState(ingestionMode)

    useEffect(() => {
        setIsIngestionMode(ingestionMode)
    }, [ingestionMode])

    const currSteps = schema

    if (lightStepper) {
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: 'inherit'
                }}>
                    <EditorSteps schema={currSteps} groupIndex={groupIndex} setGroupIndex={setGroupIndex} lightStepper={lightStepper} />
                    <EditorProgress
                        setGroupIndex={setGroupIndex}
                        onSave={() => onSave(data)}
                        isLastStep={isLastStep}
                        onComplete={onComplete}
                        disableAutoSave={disableAutoSave}
                        isReadOnly={isReadOnly}
                        onReRunAI={onReRunAI}
                        component={component}
                    />
                </div>
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                    <EditorContent paduCompiled={paduCompiled} data={data} scc={scc} stringOptions={stringOptions} setData={setData} step={currSteps[groupIndex]} ingestionMode={isIngestionMode} features={features} isReadOnly={isReadOnly} />
                </div>
            </div>
        )
    }

    return (
        <div style={{ height: 'calc(100vh - var(--top-bar-height) - 2px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <EditorSteps schema={currSteps} groupIndex={groupIndex} setGroupIndex={setGroupIndex} lightStepper={lightStepper} />
                <EditorProgress
                    setGroupIndex={setGroupIndex}
                    onSave={() => onSave(data)}
                    isLastStep={isLastStep}
                    onComplete={onComplete}
                    disableAutoSave={disableAutoSave}
                    isReadOnly={isReadOnly}
                    onReRunAI={onReRunAI}
                    component={component}
                />
            </div>
            <EditorContent data={data} scc={scc} stringOptions={stringOptions} setData={setData} step={currSteps[groupIndex]} ingestionMode={isIngestionMode} features={features} isReadOnly={isReadOnly} />
        </div>
    )
}

const EditorProgress = ({ isLastStep, onSave, onComplete, setGroupIndex, disableAutoSave, isReadOnly, onReRunAI, component }: { isLastStep: boolean, onSave: () => void, onComplete: () => void, setGroupIndex: React.Dispatch<React.SetStateAction<number>>, disableAutoSave?: boolean, isReadOnly?: boolean, onReRunAI?: () => void, component?: 'contract_editor' | 'workflow_editor' }) => {
    const theme = useTheme()

    const onNext = () => {
        if (isLastStep && !isReadOnly) {
            onSave()
            onComplete()
        } else {
            if (!isReadOnly && !disableAutoSave)
                onSave()
            setGroupIndex(v => v + 1)
        }
    }

    const getButtonText = () => {
        if (isReadOnly && !isLastStep) return 'Next'

        if (disableAutoSave) {
            return isLastStep ? 'Save' : 'Next'
        }
        return `Save & ${isLastStep ? 'Complete' : 'Next'}`
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: theme.palette.background.smartable, padding: 'var(--unit)', width: '300px', paddingRight: 20, borderLeft: 'none' }}>
            <div style={{ flex: 1 }} />
            <Stack spacing={1} direction='row'>
                <Stack justifyContent='center'>
                    {component !== 'workflow_editor' && (
                        <Tooltip
                            key='ai_icon'
                            title='Re-Run AI'>
                            <span>
                                <Button
                                    onClick={onReRunAI}>
                                    <VectorIcon sx={{
                                        justifyContent: 'flex-end'
                                    }}
                                    />
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
                <Button
                    startIcon={!isReadOnly && isLastStep ? <Save /> : undefined}
                    variant='outlined'
                    color='primary'
                    onClick={onNext}
                    disabled={isReadOnly && isLastStep}
                    style={{
                        whiteSpace: 'nowrap'
                    }}>
                    {getButtonText()}
                </Button>
            </Stack>
        </div>
    )
}

const EditorSteps = ({
    schema:
    steps,
    groupIndex,
    setGroupIndex,
    lightStepper }: {
    schema: IFormGroup[]
    groupIndex: number
    lightStepper: boolean
    setGroupIndex: React.Dispatch<React.SetStateAction<number>> }) => {
    const theme = useTheme()
    const lightStepperHeight = 'calc(var(--unit) * 10)'

    if (lightStepper) {
        return (
            <div style={{
                backgroundColor: theme.palette.background.smartable,
                padding: 'var(--unit)',
                display: 'flex',
                alignItems: 'center',
                gap: 'calc(var(--unit) * 1.33)',
                width: '100%',
                height: lightStepperHeight,
                minHeight: lightStepperHeight
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'calc(var(--unit) * 0.66)' }}>
                    <IconButton
                        onClick={() => setGroupIndex(Math.max(0, groupIndex - 1))}
                        disabled={groupIndex === 0}>
                        <ArrowBackIosNew fontSize='small' />
                    </IconButton>
                    <IconButton
                        onClick={() => setGroupIndex(Math.min(steps.length - 1, groupIndex + 1))}
                        disabled={groupIndex === steps.length - 1}>
                        <ArrowForwardIos fontSize='small' />
                    </IconButton>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'calc(var(--unit) * 0.66)' }}>
                    <Typography variant='body2' fontWeight='bold'>
                        {steps[groupIndex].label}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'calc(var(--unit) * 1.33)', width: '90%' }}>
                        <div style={{
                            flex: 1,
                            height: 4,
                            backgroundColor: theme.palette.divider,
                            borderRadius: 2
                        }}>
                            <div style={{
                                width: `${(groupIndex + 1) / steps.length * 100}%`,
                                height: '100%',
                                backgroundColor: theme.palette.primary.main,
                                transition: 'width 0.3s ease',
                                borderRadius: 'inherit'
                            }}
                            />
                        </div>
                        <Typography variant='caption' color='textSecondary'>
                            ({groupIndex + 1} of {steps.length})
                        </Typography>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Stepper activeStep={groupIndex} alternativeLabel nonLinear sx={{ backgroundColor: theme.palette.background.smartable, padding: 'var(--unit)', paddingTop: 'calc(var(--unit) * 2)', flex: 1, display: 'flex' }}>
            {steps.map((x, i) => {
                return (
                    <Step key={i}>
                        <StepButton color='inherit' onClick={() => setGroupIndex(i)}>
                            <StepLabel>
                                <span style={{ fontSize: 11, lineHeight: 1 }}>{x.label}</span>
                            </StepLabel>
                        </StepButton>
                    </Step>
                )
            })}
        </Stepper>
    )
}

const EditorContent = <T extends object>(
    {
        paduCompiled,
        step,
        data,
        scc,
        stringOptions,
        setData,
        ingestionMode,
        features,
        isReadOnly
    }:
    { step: IFormGroup
        paduCompiled?: IPADUCompiled | null
        data: T
        stringOptions: FormBuilderOptions<T>
        scc: FormBuilderSCC<T>
        setData: React.Dispatch<React.SetStateAction<T>>
        ingestionMode?: boolean
        features?: IClassifiedFeature[]
        isReadOnly: boolean
    }

) => {
    const containerRef = useRef<HTMLDivElement>()
    const theme = useTheme()
    const [modifiedFields, setModifiedFields] = useState({})

    const handleFieldChange = fieldKey => {
        setModifiedFields(prev => ({
            ...prev,
            [fieldKey]: true
        }))
    }

    const getFieldBlock = (field: IFormGroup['fields'][0]) => {
        const normalize = (key: string) =>
            ContractsManager
                .removeIndex(key.trim().toLowerCase())
                .replace(/\.$/, '')

        const fieldKeyToMatch = (field as any).fullKey || field.key
        const keyClean = normalize(fieldKeyToMatch)

        let candidates = []

        if (field.type === 'list') {
            candidates = features?.filter(f => {
                const featureName = normalize(f.name)

                return (
                    featureName.startsWith(keyClean + '[')
                    || featureName === keyClean
                ) &&
                f.value != null &&
                f.value !== ''
            }) ?? []
        } else {
            candidates = features?.filter(f => {
                const featureKeyClean = normalize(f.name)

                return featureKeyClean === keyClean &&
                    f.value != null &&
                    f.value !== ''
            }) ?? []
        }

        const isFeatureFromAI = candidates[0] ?? undefined
        const confidence = isFeatureFromAI?.confidence || 1
        const isModified = modifiedFields[field.key]

        const getConfidenceColor = (confidence: number) => {
            const red = Math.max(0, 255 - Math.floor(confidence * 225))
            const green = Math.min(255, Math.floor(confidence * 255))
            return `rgb(${red}, ${green}, 50)`
        }

        return (
            <div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 'var(--unit)',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                }}>
                    {field.type === 'list' && <EditNoteRounded sx={{ marginRight: 2 }} />}

                    <Typography fontWeight='bold'>
                        {field.label}
                    </Typography>

                    <Box pl='1'>
                        <PADUChip
                            compiledPADU={
                                paduCompiled?.fields.find(
                                    x => x.key === (field as any).fullKey || x.key === field.key
                                )
                            }
                        />
                    </Box>

                    {ingestionMode && field.type !== 'list' && (
                        <Tooltip title='AI Icon' key='ai_icon'>
                            <span>
                                <VectorIcon
                                    style={{
                                        color: isModified
                                            ? theme.palette.grey[500]
                                            : (isFeatureFromAI
                                                    ? getConfidenceColor(confidence)
                                                    : theme.palette.grey[500]),
                                        justifyContent: 'flex-end'
                                    }}
                                />
                            </span>
                        </Tooltip>
                    )}
                </div>

                <Typography fontWeight={400} fontSize='.8rem' mt={1}>
                    {field.description}
                </Typography>
            </div>
        )
    }

    const getChildEditor = (item: any, field: IFormField, index: number, onChange: (newValue: any) => void) => {
        // TODO: Temporary fix
        const rawChildItems = FormHelper.getValue(item, field.key, index, step) ?? []
        const childItems = Array.isArray(rawChildItems) ? rawChildItems : []

        return (
            <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 'calc(var(--unit) * 2)' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', gap: 'calc(var(--unit) * 2)' }}>
                    <div style={{ flex: 0.45 }}>
                        {getFieldBlock(field)}
                    </div>
                    <div style={{ flex: 0.55 }}>
                        <FieldEditor<T>
                            item={item}
                            onChange={onChange}
                            field={field}
                            index={index}
                            stringOptions={stringOptions}
                            scc={scc}
                            step={step}
                            ingestionMode={ingestionMode}
                            features={features}
                            isReadOnly={isReadOnly}
                        />
                    </div>
                </div>
                {childItems?.map((x, i) => {
                    const hasErrors = field.childFields?.some(cf => {
                        const childKey = cf.key.split('.').pop()
                        const childValue = x[childKey]
                        const { isError } = FormHelper.validateField(cf, childValue, x, i, step)
                        return isError
                    }) ?? false

                    return (
                        <TreeViewer
                            defaultOpen={hasErrors}
                            disableDeletion={isReadOnly}
                            key={i}
                            onDelete={() => {
                                const newList = _.clone(childItems)
                                newList.splice(i, 1)
                                const res = FormHelper.setValue(item, field.key, newList, step)
                                onChange(res)
                            }}
                            head={(
                                <Typography fontWeight='bold'>
                                    {field.label}
                                    {' '}
                                    #
                                    {i + 1}
                                </Typography>
                            )}
                            body={(
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'calc(var(--unit) * 3)' }}>
                                    {field.childFields?.map((cf, j) => {
                                        const relativeField = {
                                            ...cf,
                                            key: cf.key.startsWith(field.key + '.')
                                                ? cf.key.substring(field.key.length + 1)
                                                : cf.key,
                                            fullKey: cf.key
                                        }

                                        return (
                                            <div key={cf.key + j}>
                                                {getField(x, relativeField, i, nv => {
                                                    const newChilds = structuredClone(childItems)
                                                    newChilds.splice(i, 1, nv)

                                                    const newParent = FormHelper.setValue(item, field.key, newChilds, step)
                                                    onChange(newParent)
                                                })}
                                                {j < (field.childFields?.length - 1) &&
                                                    <Divider sx={{ mt: 3 }} />}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        />
                    )
                })}
            </div>
        )
    }

    const getObjectEditor = (item: any, field: IFormField, index: number, onChange: (newValue: any) => void) => {
        let currentObject = FormHelper.getValue(item, field.key, index, step)

        if (!currentObject) {
            currentObject = {}
        }
        const hasRequiredErrors = field.objectFields?.some(of => {
            const childValue = currentObject[of.key.split('.').pop()]
            const { isError } = FormHelper.validateField(of, childValue, currentObject, index, step)
            return isError
        }) ?? false

        return (
            <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 'calc(var(--unit) * 2)' }}>
                <TreeViewer
                    defaultOpen={hasRequiredErrors}
                    head={<Typography fontWeight='bold'>{field.label}</Typography>}
                    body={(
                        <>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', gap: 'calc(var(--unit) * 2)' }}>
                                <Typography fontWeight={400} fontSize='.8rem' mb={1}>{field.description}</Typography>
                            </div>
                            <Divider sx={{ mb: 2, mt: 1 }} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'calc(var(--unit) * 2)' }}>
                                {field.objectFields?.map((of, j) => {
                                    const relativeField = {
                                        ...of,
                                        key: of.key.split('.').pop() || of.key,
                                        fullKey: of.key
                                    }

                                    return (
                                        <div key={of.key}>
                                            {getField(currentObject, relativeField, j, updatedObject => {
                                                const res = FormHelper.setValue(item, field.key, updatedObject, step)
                                                onChange(res)
                                            })}
                                            {j < (field.objectFields?.length - 1) &&
                                                <Divider sx={{ mt: 3 }} />}
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                />
            </div>
        )
    }

    const getField = (
        item: any,
        field: IFormField,
        index: number,
        onChange: (newValue: any) => void
    ) => {
        return (
            <div key={field.key} className='master-editor-field'>
                {field.type === 'list'
                    ? getChildEditor(item, field, index, onChange)
                    : field.type === 'object'
                        ? getObjectEditor(item, field, index, onChange)
                        : (
                                <>
                                    <div style={{ flex: 0.45, alignSelf: 'flex-start' }}>
                                        {getFieldBlock(field)}
                                    </div>
                                    <div style={{ flex: 0.55 }}>
                                        <FieldEditor
                                            item={item}
                                            onChange={newValue => {
                                                onChange(newValue)
                                                handleFieldChange(field.key)
                                            }}
                                            field={field}
                                            stringOptions={stringOptions}
                                            index={index}
                                            scc={scc}
                                            features={features}
                                            ingestionMode={ingestionMode}
                                            step={step}
                                            isReadOnly={isReadOnly}
                                        />
                                    </div>
                                </>
                            )}
            </div>
        )
    }
    return (
        <div
            ref={containerRef}
            style={{
                padding: 'calc(var(--unit) * 4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'calc(var(--unit) * 3)'
            }}>
            {step.fields.map((field, i) => {
                return (
                    <React.Fragment key={i}>
                        {getField(data, field, i, nv => setData(nv))}
                        {i < (step.fields.length - 1) &&
                            <Divider sx={{ mt: 1 }} />}
                    </React.Fragment>
                )
            })}
        </div>
    )
}

export default EditorForm
